// src/components/pipelines/StageDraftList.tsx
//
// Edit a pipeline's stages BEFORE it exists — the New Pipeline form. Purely
// local state; the whole list is sent with the create request and the server
// creates pipeline + stages in one transaction.
//
// Reordering works two ways and neither depends on the other: drag a row
// (pointer, or keyboard via the drag handle — space to lift, arrows, space to
// drop), or use the labelled Move up / Move down buttons.

import { DragDropContext, Droppable, Draggable, type DropResult, type DroppableProvided } from '@hello-pangea/dnd';
import { useId } from 'react';
import { OUTCOME_SHORT, moveItem, type StageDraft } from './stageOutcome';
import type { StageOutcome } from '../../types/api';

interface Props {
  stages: StageDraft[];
  onChange: (stages: StageDraft[]) => void;
  disabled?: boolean;
}

let keySeq = 0;

export default function StageDraftList({ stages, onChange, disabled }: Props) {
  const baseId = useId();

  const update = (index: number, patch: Partial<StageDraft>) =>
    onChange(stages.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const onDragEnd = (r: DropResult) => {
    if (!r.destination) return;
    onChange(moveItem(stages, r.source.index, r.destination.index));
  };

  const addStage = () => {
    // A new open stage belongs before the closing stages.
    const firstClosed = stages.findIndex((s) => s.outcome !== 'open');
    const at = firstClosed === -1 ? stages.length : firstClosed;
    const next = [...stages];
    next.splice(at, 0, { key: `new-${++keySeq}`, name: '', outcome: 'open', probability: null });
    onChange(next);
  };

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-sm font-medium text-gray-700 mb-1">Stages</legend>
      <p className="text-xs text-gray-500">
        Deals move left to right through these stages. Mark one stage <strong>Won</strong> and one{' '}
        <strong>Lost</strong> to close deals; every other stage is open.
      </p>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`${baseId}-stages`}>
          {(provided: DroppableProvided) => (
            <ol ref={provided.innerRef} {...provided.droppableProps} className="space-y-1.5" aria-label="Stages in order">
              {stages.map((stage, index) => (
                <Draggable key={stage.key} draggableId={stage.key} index={index} isDragDisabled={disabled}>
                  {(drag) => (
                    <li
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5"
                    >
                      <span
                        {...drag.dragHandleProps}
                        aria-label={`Drag ${stage.name || 'stage'} to reorder`}
                        className="cursor-grab select-none px-1 text-gray-400"
                      >
                        ⋮⋮
                      </span>
                      <label className="sr-only" htmlFor={`${baseId}-name-${stage.key}`}>
                        Stage {index + 1} name
                      </label>
                      <input
                        id={`${baseId}-name-${stage.key}`}
                        value={stage.name}
                        onChange={(e) => update(index, { name: e.target.value })}
                        placeholder="Stage name"
                        maxLength={100}
                        className="min-w-0 flex-1 basis-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                      <label className="sr-only" htmlFor={`${baseId}-outcome-${stage.key}`}>
                        Stage {index + 1} outcome
                      </label>
                      <select
                        id={`${baseId}-outcome-${stage.key}`}
                        value={stage.outcome}
                        onChange={(e) => update(index, { outcome: e.target.value as StageOutcome })}
                        className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                      >
                        {(['open', 'won', 'lost'] as const).map((o) => (
                          <option key={o} value={o}>
                            {OUTCOME_SHORT[o]}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => onChange(moveItem(stages, index, index - 1))}
                          disabled={index === 0}
                          aria-label={`Move ${stage.name || `stage ${index + 1}`} up`}
                          className="h-8 w-8 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => onChange(moveItem(stages, index, index + 1))}
                          disabled={index === stages.length - 1}
                          aria-label={`Move ${stage.name || `stage ${index + 1}`} down`}
                          className="h-8 w-8 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => onChange(stages.filter((_, i) => i !== index))}
                          disabled={stages.length <= 1}
                          aria-label={`Remove ${stage.name || `stage ${index + 1}`}`}
                          className="h-8 w-8 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ol>
          )}
        </Droppable>
      </DragDropContext>
      <button type="button" onClick={addStage} className="text-sm text-blue-600 hover:text-blue-700">
        + Add stage
      </button>
    </fieldset>
  );
}
